// "Copy profile link" on the instructor dashboard (IW-2): the public address of their profile, so
// they can bring their own students. Three states:
// - copied: the address is on the clipboard and "Link copied" shows;
// - clipboard blocked (or absent): the address shows selected in a read-only field to copy by hand;
// - not listed: the public profile 404s until the instructor is listed, so the button is disabled.
import React from 'react';
import { Btn } from './index';
import { instructorProfileUrl } from '../lib/routes';
import { palette, type } from '../design';

// An instructor is listed in the marketplace only in this status (mirrors INSTRUCTOR_STATUS.ACTIVE).
const LISTED_STATUS = 'active';

const FONT = "'Plus Jakarta Sans', sans-serif";

export const COPIED_MESSAGE = 'Link copied';
export const BLOCKED_MESSAGE = 'We could not copy your link. It is selected below, so copy it by hand.';
export const NOT_LISTED_MESSAGE = 'Your profile link works once your profile is listed.';

const COPY_STATE = { IDLE: 'idle', COPIED: 'copied', BLOCKED: 'blocked' };

export function CopyProfileLinkButton({ instructor }) {
  const [state, setState] = React.useState(COPY_STATE.IDLE);
  const fieldRef = React.useRef(null);

  // Select the link the moment the fallback field appears, so one Ctrl+C or long-press copies it.
  React.useEffect(() => {
    if (state === COPY_STATE.BLOCKED && fieldRef.current) {
      fieldRef.current.focus();
      fieldRef.current.select();
    }
  }, [state]);

  // The dashboard passes `{}` while its data loads: show nothing rather than a false "not listed".
  if (!instructor?.id) return null;
  const listed = instructor.status === LISTED_STATUS;
  const url = instructorProfileUrl(instructor);

  const onCopy = async () => {
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(url);
      setState(COPY_STATE.COPIED);
    } catch {
      // Not silent: the instructor gets the link in a field to copy by hand.
      setState(COPY_STATE.BLOCKED);
    }
  };

  const note = { fontFamily: FONT, fontSize: type.label, lineHeight: 1.5, margin: 0 };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, width: '100%' }}>
      <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 10 }}>
        <Btn size="sm" variant="ghost" onClick={onCopy} disabled={!listed}>Copy profile link</Btn>
        {!listed && <p style={{ ...note, color: palette.walnut }}>{NOT_LISTED_MESSAGE}</p>}
        {listed && state !== COPY_STATE.IDLE && (
          <p role="status" style={{ ...note, fontWeight: 600, color: state === COPY_STATE.COPIED ? palette.sageGreen : palette.walnut }}>
            {state === COPY_STATE.COPIED ? `✓ ${COPIED_MESSAGE}` : BLOCKED_MESSAGE}
          </p>
        )}
      </div>
      {listed && state === COPY_STATE.BLOCKED && (
        <input
          ref={fieldRef}
          type="text"
          readOnly
          aria-label="Your profile link"
          value={url}
          onFocus={(e) => e.target.select()}
          style={{ width: '100%', maxWidth: 520, boxSizing: 'border-box', padding: '8px 12px', borderRadius: 10, border: '1px solid ' + palette.border, fontFamily: FONT, fontSize: type.body, color: palette.walnut, background: palette.white }}
        />
      )}
    </div>
  );
}
